import { FeedbackBanner } from "@/core/components/feedback-banner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { PollingResult } from "./polling";
import type { PayrollStatementProjectionBuild } from "./types";

export const ProjectionBuildBanner = ({
  build,
  canCheckAgain,
  isRefreshing,
  isRetrying,
  onCheckAgain,
  onRetry,
}: {
  build: PollingResult<PayrollStatementProjectionBuild> | null;
  canCheckAgain: boolean;
  isRefreshing: boolean;
  isRetrying: boolean;
  onCheckAgain: () => void;
  onRetry: () => void;
}) => {
  if (
    !build ||
    build.status === "ready" ||
    (build.status === "pending" && !build.polling_timed_out && !canCheckAgain)
  ) {
    return null;
  }

  if (build.status === "failed") {
    return (
      <FeedbackBanner variant="error">
        <Stack direction="row" align="center" justify="between" gap="md">
          <Stack gap="xs">
            <Typography variant="bodyW3">PayPack build failed.</Typography>
            <Typography variant="bodyW2" textColor="secondary">
              {build.error ?? "The shared statement build couldn't finish."}
            </Typography>
          </Stack>
          {build.retryable ? (
            <Button
              size="sm"
              variant="secondary"
              loading={isRetrying}
              onClick={onRetry}
            >
              Retry build
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              loading={isRefreshing}
              onClick={onCheckAgain}
            >
              Check again
            </Button>
          )}
        </Stack>
      </FeedbackBanner>
    );
  }

  if (build.polling_timed_out) {
    return (
      <FeedbackBanner variant="warning">
        <Stack direction="row" align="center" justify="between" gap="md">
          <Stack gap="xs">
            <Typography variant="bodyW3">
              PayPack building is taking longer than expected.
            </Typography>
            <Typography variant="bodyW2" textColor="secondary">
              Check again to see if the PayPack build is ready.
            </Typography>
          </Stack>
          <Button
            size="sm"
            variant="secondary"
            loading={isRefreshing}
            onClick={onCheckAgain}
          >
            Check again
          </Button>
        </Stack>
      </FeedbackBanner>
    );
  }

  return (
    <FeedbackBanner variant="warning">
      <Stack direction="row" align="center" justify="between" gap="md">
        <Stack gap="xs">
          <Typography variant="bodyW3">
            PayPack data could not finish loading.
          </Typography>
          <Typography variant="bodyW2" textColor="secondary">
            Check again to load the completed PayPacks.
          </Typography>
        </Stack>
        <Button
          size="sm"
          variant="secondary"
          loading={isRefreshing}
          onClick={onCheckAgain}
        >
          Check again
        </Button>
      </Stack>
    </FeedbackBanner>
  );
};
